const EMPTY_VISIBLE_CHAT = Object.freeze({
	chatLastText: "",
	chatLastTextUser: "",
	chatLastTextTime: null,
	chatPinnedCount: 0,
	chatUnreadCount: 0,
	chatFollowUp: false
});

function toNonNegativeInteger(value) {
	const numberValue = Number(value);
	return Number.isFinite(numberValue) ? Math.max(0, Math.trunc(numberValue)) : 0;
}

function getStreamUnreadChannelReference(unreadChannel = {}) {
	return unreadChannel.channel_id
		|| unreadChannel.channel_cid
		|| unreadChannel.cid
		|| (unreadChannel.channel && (unreadChannel.channel.cid || unreadChannel.channel.id))
		|| "";
}

export function isStatusChatChannelId(channelId = "") {
	const rawChannelId = String(channelId === undefined || channelId === null ? "" : channelId).trim();
	const channelTypeSeparator = rawChannelId.indexOf(":");
	const channelType = channelTypeSeparator === -1
		? null
		: rawChannelId.slice(0, channelTypeSeparator);
	const normalizedChannelId = channelTypeSeparator === -1
		? rawChannelId
		: rawChannelId.slice(channelTypeSeparator + 1);

	// Status opens job chat using the reserved `team:instance-*` convention.
	// Bare IDs are what Stream's /unread endpoint normally returns; if a full
	// CID is supplied, do not mistake another channel type for a Status channel.
	return (!channelType || channelType === "team")
		&& /^instance-.+/.test(normalizedChannelId);
}

export function normalizeStreamUnreadChannelId(unreadChannel = {}) {
	const rawChannelId = getStreamUnreadChannelReference(unreadChannel);
	const channelId = String(rawChannelId).trim();
	const channelTypeSeparator = channelId.indexOf(":");

	return channelTypeSeparator === -1
		? channelId
		: channelId.slice(channelTypeSeparator + 1);
}

export function getStatusChatInstanceId(channelReference = "") {
	if (!isStatusChatChannelId(channelReference)) {
		return "";
	}

	const channelId = normalizeStreamUnreadChannelId({ channel_id: channelReference });
	const rawInstanceId = channelId.slice("instance-".length);
	return rawInstanceId.endsWith("-internal")
		? rawInstanceId.slice(0, -"-internal".length)
		: rawInstanceId;
}

export function getStreamChatUnreadBreakdown(unreadResponse = {}) {
	const hasUnreadChannelList = Array.isArray(unreadResponse.channels);
	const unreadChannels = hasUnreadChannelList ? unreadResponse.channels : [];
	let chatUnreadMessagesCount = hasUnreadChannelList ? 0 : null;
	let statusUnreadMessagesCount = hasUnreadChannelList ? 0 : null;
	let jobUnreadMessagesCount = 0;
	const jobUnreadByInstanceId = {};
	let excludedAiUnreadMessagesCount = 0;
	let navigationFallbackUnreadMessagesCount = hasUnreadChannelList ? 0 : null;

	unreadChannels.forEach(unreadChannel => {
		const channelReference = getStreamUnreadChannelReference(unreadChannel);
		const channelId = normalizeStreamUnreadChannelId(unreadChannel);
		const unreadCount = toNonNegativeInteger(unreadChannel && unreadChannel.unread_count);

		if (!channelId || unreadCount === 0) {
			return;
		}

		if (channelId.indexOf("ai-dm-") === 0) {
			excludedAiUnreadMessagesCount += unreadCount;
			return;
		}

		// This is the count known to be reachable from the visible Chat UI. It is
		// retained as a conservative fallback if Stream truncates the ID list.
		navigationFallbackUnreadMessagesCount += unreadCount;

		if (isStatusChatChannelId(channelReference)) {
			statusUnreadMessagesCount += unreadCount;
			jobUnreadMessagesCount += unreadCount;
			const instanceId = getStatusChatInstanceId(channelReference);
			jobUnreadByInstanceId[instanceId] = toNonNegativeInteger(jobUnreadByInstanceId[instanceId]) + unreadCount;
		} else {
			// Direct Messages and ordinary team Channels are both available from
			// Chat. Only the reserved instance-* convention identifies a channel
			// that is also reachable from a Status record.
			chatUnreadMessagesCount += unreadCount;
		}
	});

	const totalUnreadMessagesCount = unreadResponse.total_unread_count;
	if (hasUnreadChannelList
		&& Number.isFinite(totalUnreadMessagesCount)
		&& (chatUnreadMessagesCount + statusUnreadMessagesCount + excludedAiUnreadMessagesCount) < toNonNegativeInteger(totalUnreadMessagesCount)) {
		// Stream may truncate the per-channel list. Without an ID for every unread
		// conversation, the remainder cannot be assigned safely to Chat or Status.
		chatUnreadMessagesCount = null;
		statusUnreadMessagesCount = null;
	}

	return {
		chatUnreadMessagesCount,
		statusUnreadMessagesCount,
		navigationFallbackUnreadMessagesCount,
		jobUnreadMessagesCount,
		jobUnreadByInstanceId
	};
}

export function resolveNavigationChatUnreadCounts({
	chatUnreadMessagesCount,
	statusUnreadMessagesCount,
	fallbackUnreadMessagesCount
} = {}) {
	if (Number.isFinite(chatUnreadMessagesCount)
		&& Number.isFinite(statusUnreadMessagesCount)) {
		return {
			chatUnreadCount: toNonNegativeInteger(chatUnreadMessagesCount),
			statusUnreadCount: toNonNegativeInteger(statusUnreadMessagesCount)
		};
	}

	// The connection aggregate cannot distinguish instance-backed Status chats
	// from Direct Messages, general Channels, or hidden AI conversations. If the
	// exact ID split is unavailable, use only the known visible count and place it
	// on Chat—the one destination where every visible conversation is available.
	return {
		chatUnreadCount: Number.isFinite(fallbackUnreadMessagesCount)
			? toNonNegativeInteger(fallbackUnreadMessagesCount)
			: 0,
		statusUnreadCount: 0
	};
}

export function getVisibleChatInstanceIds(hits = []) {
	const seen = new Set();
	const instanceIds = [];

	(Array.isArray(hits) ? hits : []).forEach(hit => {
		const instanceId = String(hit && hit.instanceId !== undefined && hit.instanceId !== null
			? hit.instanceId
			: "").trim();
		if (!instanceId || seen.has(instanceId)) {
			return;
		}

		seen.add(instanceId);
		instanceIds.push(instanceId);
	});

	return instanceIds;
}

export function applyVisibleJobUnreadCounts(chatState = {}, instanceIds = [], jobUnreadByInstanceId = null) {
	const normalizedInstanceIds = Array.isArray(instanceIds)
		? instanceIds.map(instanceId => String(instanceId || "").trim()).filter(Boolean)
		: [];
	const hasExactUnreadMap = !!jobUnreadByInstanceId && typeof jobUnreadByInstanceId === "object";
	const nextChat = {};

	normalizedInstanceIds.forEach(instanceId => {
		const current = chatState && chatState[instanceId] ? chatState[instanceId] : EMPTY_VISIBLE_CHAT;
		nextChat[instanceId] = {
			...EMPTY_VISIBLE_CHAT,
			...current,
			chatUnreadCount: hasExactUnreadMap
				? toNonNegativeInteger(jobUnreadByInstanceId[instanceId])
				: toNonNegativeInteger(current.chatUnreadCount)
		};
	});

	return nextChat;
}

export function buildVisibleJobChatState(instanceIds = [], channels = [], jobUnreadByInstanceId = null) {
	const normalizedInstanceIds = Array.isArray(instanceIds)
		? instanceIds.map(instanceId => String(instanceId || "").trim()).filter(Boolean)
		: [];
	const nextChat = {};
	const stateKeyByChannelId = new Map();

	normalizedInstanceIds.forEach(instanceId => {
		nextChat[instanceId] = { ...EMPTY_VISIBLE_CHAT };
		stateKeyByChannelId.set(instanceId, instanceId);
		stateKeyByChannelId.set(instanceId.indexOf("instance-") === 0 ? instanceId : `instance-${instanceId}`, instanceId);
	});

	(Array.isArray(channels) ? channels : []).forEach(channel => {
		const rawChannelId = String((channel && (channel.id || channel.cid)) || "").trim();
		const channelId = rawChannelId.indexOf(":") === -1
			? rawChannelId
			: rawChannelId.slice(rawChannelId.indexOf(":") + 1);
		const stateKey = stateKeyByChannelId.get(channelId);
		if (!stateKey) {
			return;
		}

		const messageSet = channel && channel.state && Array.isArray(channel.state.messageSets)
			? channel.state.messageSets[0]
			: null;
		const messages = messageSet && Array.isArray(messageSet.messages)
			? messageSet.messages
			: (channel && channel.state && Array.isArray(channel.state.messages) ? channel.state.messages : []);
		const latestMessage = messages.length > 0 ? messages[messages.length - 1] : null;

		nextChat[stateKey] = {
			chatLastText: latestMessage && latestMessage.text ? latestMessage.text : "",
			chatLastTextUser: latestMessage && latestMessage.user && latestMessage.user.name ? latestMessage.user.name : "",
			chatLastTextTime: latestMessage && latestMessage.created_at ? latestMessage.created_at : null,
			chatPinnedCount: channel && channel.state && Array.isArray(channel.state.pinnedMessages)
				? channel.state.pinnedMessages.length
				: 0,
			chatUnreadCount: toNonNegativeInteger(channel && channel.state && channel.state.unreadCount),
			chatFollowUp: false
		};
	});

	return applyVisibleJobUnreadCounts(nextChat, normalizedInstanceIds, jobUnreadByInstanceId);
}
